import { EmptyView, emptyViewVariants } from '@components'

import { ModuleLayout } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { EmberRoute } from '../../../../enums/emberRoute'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { useEmberRouteUrl } from '../../../../hooks/routing/useEmberRouteUrl'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { defaultTransactionsQuery } from '../../constants/defaultTransactionsQuery'
import { useTransactionsEmberConnection } from '../../contexts/transactionsEmberConnectionContext'
import { useTransactionsListFilters } from '../../contexts/transactionsListFiltersContext'
import { useIsTransactionsListEmpty } from '../TransactionsList/hooks/useIsTransactionsListEmpty'

export const TransactionsListEmpty = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { setFilters } = useTransactionsListFilters()
  const { handleAddTransaction } = useTransactionsEmberConnection()
  const isTransactionsListEmpty = useIsTransactionsListEmpty()
  const { url: transactionsListUrl } = useOrganizationViewUrl(reactRoute.organization.transactions.getMainRoute())
  const { url: invoicesNewUrl } = useEmberRouteUrl(EmberRoute.InvoicesNew)

  const canCreateTransaction = isAuthorized(Scope.TransactionWrite)
  const shouldSeeCreateButton = canCreateTransaction || isAuthorized(Scope.InvoiceWrite)

  // Clear filters in case they left after redirecting from `TransactionsList`
  useEffect(() => {
    setFilters(defaultTransactionsQuery)
  }, [setFilters])

  const { animation, description, title, buttonText: defaultButtonText } = emptyViewVariants.transactions
  const buttonText = canCreateTransaction ? 'empty_state.transactions.add_new_entry' : defaultButtonText

  const handleEmptyViewClick = useCallback(() => {
    if (canCreateTransaction) {
      track('xxx - Bookkeeping - Ledger', { action: 'Add new entry click' })
      handleAddTransaction()
      return
    }

    track('xxx - Bookkeeping - Ledger', { action: 'Create new invoice click' })

    if (invoicesNewUrl) {
      navigate(invoicesNewUrl)
    }
  }, [canCreateTransaction, handleAddTransaction, invoicesNewUrl, navigate, track])

  if (!isTransactionsListEmpty && transactionsListUrl) {
    return <Navigate to={transactionsListUrl} replace />
  }

  return (
    <ModuleLayout title={t('transactions')}>
      <EmptyView
        animationName={animation}
        onClick={handleEmptyViewClick}
        showCTA={shouldSeeCreateButton}
        tButton={buttonText}
        tDescription={description}
        tTitle={title}
      />
    </ModuleLayout>
  )
}
